import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { makeConfig, PaginatedTable2 } from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { ReceiptBatch } from '@pflegenavi/frontend/api-nursing-home';
import {
  useGetPaymentProcessor,
  useGetReceiptBatchListPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { useGetBatchReceiptDateString } from '@pflegenavi/shared/frontend';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { LinkedTableSection } from './LinkedTableSection';
import { ServiceProviderLink } from './ServiceProviderLink';
import { useLinkedTablePagination } from './useLinkedTablePagination';

type ColumnNames =
  | 'serviceProvider'
  | 'receiptType'
  | 'receipts'
  | 'amount'
  | 'additionalFeeAmount'
  | 'receiptDate'
  | 'submissionDate';

/**
 * Only open batches count towards the linked amount: once a batch is
 * submitted, the receipts created from it are linked and counted instead.
 */
export const ReceiptBatchTable: FC<{
  variant: 'open' | 'submitted';
  ids: string[];
}> = ({ variant, ids }) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const navigate = useNavigate();
  const paymentProcessor = useGetPaymentProcessor();
  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();

  const { page, pageSize, pagination } = useLinkedTablePagination(ids.length);

  const { data, isLoading } = useGetReceiptBatchListPhoenix({
    params: { ids, page: page + 1, page_size: pageSize },
  });

  const config = useMemo(
    () =>
      makeConfig<ReceiptBatch, ColumnNames, never>({
        showAll: undefined,
        hideEmptyRows: true,
        onClickRow: (row) =>
          navigate(
            generatePath(nhAppMainPages.RECEIPTS_BATCH_EDIT, {
              batchId: row.id,
            })
          ),
        columns: [
          {
            key: 'serviceProvider',
            label: t('receipts.batch.overview-table.columns.service-provider'),
            align: 'left',
            component: ({ row }) => (
              <ServiceProviderLink
                id={row.service_provider?.id}
                name={row.service_provider?.name ?? '--'}
              />
            ),
          },
          {
            key: 'receiptType',
            label: t('receipts.type'),
            align: 'left',
            component: ({ row }) =>
              row.receipt_type
                ? t(`receiptType.${row.receipt_type.name}`)
                : '--',
          },
          {
            key: 'receipts',
            label: t('receipts.batch.overview-table.columns.receipts'),
            align: 'left',
            component: ({ row }) =>
              t('receipts.batch.overview-table.receipts-count', {
                count: row.entry_count,
              }),
          },
          {
            key: 'amount',
            label: t('receipts.batch.overview-table.columns.amount'),
            align: 'right',
            component: ({ row }) => (
              <Typography variant="subtitle2">
                {fCurrencyCents(row.total_amount)}
              </Typography>
            ),
          },
          ...(paymentProcessor === 'mangopay'
            ? [
                {
                  key: 'additionalFeeAmount' as const,
                  label: t(
                    'receipts.batch.overview-table.columns.additional-fee-amount'
                  ),
                  align: 'right' as const,
                  component: ({ row }: { row: ReceiptBatch }) =>
                    row.total_additional_fee_amount !== 0
                      ? fCurrencyCents(row.total_additional_fee_amount)
                      : '--',
                },
              ]
            : []),
          {
            key: 'receiptDate',
            label: t('common.receipt-date'),
            align: 'center',
            component: ({ row }) =>
              getBatchReceiptDateString(
                row.use_individual_dates ?? false,
                row.min_receipt_date,
                row.max_receipt_date,
                row.receipt_date ?? new Date()
              ),
          },
          ...(variant === 'submitted'
            ? [
                {
                  key: 'submissionDate' as const,
                  label: t(
                    'receipts.batch.overview-table.columns.submissionDate'
                  ),
                  align: 'center' as const,
                  component: ({ row }: { row: ReceiptBatch }) =>
                    row.job?.submission_date
                      ? fDate(
                          row.job.submission_date,
                          YEAR_MONTH_DAY_SHORT_FORMAT
                        )
                      : '--',
                },
              ]
            : []),
        ],
        pagination,
      }),
    [
      t,
      navigate,
      fCurrencyCents,
      fDate,
      getBatchReceiptDateString,
      paymentProcessor,
      variant,
      pagination,
    ]
  );

  return (
    <LinkedTableSection
      title={t(
        variant === 'open'
          ? 'cashManagement.group-detail.overview.linked-open-receipt-batches-title'
          : 'cashManagement.group-detail.overview.linked-submitted-receipt-batches-title'
      )}
      count={ids.length}
      hint={
        variant === 'submitted'
          ? t(
              'cashManagement.group-detail.linked-submitted-receipt-batches-hint'
            )
          : undefined
      }
    >
      <PaginatedTable2
        name={
          variant === 'open'
            ? 'CashTransactionGroupDetailOpenReceiptBatchTable'
            : 'CashTransactionGroupDetailSubmittedReceiptBatchTable'
        }
        config={config}
        rows={data?.data ?? []}
        isLoading={isLoading}
      />
    </LinkedTableSection>
  );
};
